import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';
import {
  Building2Icon,
  CameraIcon,
  DollarSignIcon,
  FileIcon,
  FilePlus2Icon,
  FileTextIcon,
  HammerIcon,
  HouseIcon,
  IdCardIcon,
  MapPinIcon,
  MessageCircleIcon,
  MessageSquareTextIcon,
  PercentIcon,
  ReceiptTextIcon,
  Rows3Icon,
  Share2Icon,
  TrendingUpIcon,
  UsersIcon,
} from 'lucide-react';

import {
  Doc_Template_Type_Enum_Enum,
  type GetCmaReportForPreviewQuery,
} from '@graphql/__generated__/graphql';
import ReportEditor from '@modules/_shared/components/report-editor/ReportEditor';
import type { StepType } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useGetDefaultApolloTemplate } from '@modules/_shared/utils/template';
import {
  CMA_REPORT_INCLUDE_FRAGMENT,
  DOC_TEMPLATES_CMA_REPORT,
  UPDATE_CMA_REPORT_DOC_TEMPLATE,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import StepAgencyProfile from '@modules/reports/cma-reports/step-agent/StepAgencyProfile';
import StepAgentProfile from '@modules/reports/cma-reports/step-agent/StepAgentProfile';
import StepClientTestimonials from '@modules/reports/cma-reports/step-agent/StepClientTestimonials';
import StepHedonisticValuation from '@modules/reports/cma-reports/step-listing-appraise/StepHedonisticValuation';
import RentalListings from '@modules/reports/cma-reports/step-listing-compare/RentalListings';
import StepListingsCompare from '@modules/reports/cma-reports/step-listing-compare/StepListingsCompare';
import StepTransactions from '@modules/reports/cma-reports/step-transaction/StepTransactions';
import StepAppendFiles from '@modules/reports/cma-reports/StepAppendFiles';
import StepCapRateValuation from '@modules/reports/cma-reports/StepCapRateValuation';
import StepCoverPhotoCMA from '@modules/reports/cma-reports/StepCoverPhotoCMA';
import StepDescription from '@modules/reports/cma-reports/StepDescription';
import StepFinal from '@modules/reports/cma-reports/StepFinal';
import StepIntrinsicValuation from '@modules/reports/cma-reports/StepIntrinsicValuation';
import StepIntroduction from '@modules/reports/cma-reports/StepIntroduction';
import StepMarketData from '@modules/reports/cma-reports/StepMarketData';
import StepPhotos from '@modules/reports/cma-reports/StepPhotosCMA';
import StepPOICMA from '@modules/reports/cma-reports/StepPOICMA';
import StepPotentialBuyers from '@modules/reports/cma-reports/StepPotentialBuyers';
import StepPropertyDetailsCMA from '@modules/reports/cma-reports/StepPropertyDetailsCMA';
import StepShareReport from '@modules/reports/shared/StepShareReport';
import {
  type CMAPageId,
  extractFieldsFromFragment,
} from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

type CMAReportEditorWorkflowProps = {
  cmaReport: NonNullable<GetCmaReportForPreviewQuery['cma_reports_by_pk']>;
  previewOnly?: boolean;
  previewVersion?: string;
};

const activeStepIconProps = { size: 20, strokeWidth: 2.75 };

const CMAReportEditorWorkflow = ({
  cmaReport,
  previewOnly = false,
  previewVersion,
}: CMAReportEditorWorkflowProps) => {
  const { t, countryCode } = useLocale();
  const documentId = toGlobalId('CMAReport', cmaReport.id);

  const { me } = useAppData();
  const { getDefaultTemplate } = useGetDefaultApolloTemplate();

  const canInitializeTemplate =
    cmaReport.doc_template == null &&
    (cmaReport.doc_template_id == null ||
      (cmaReport.broker_id === me?.id && !previewOnly));

  const { data: docTemplatesData } = useQuery(DOC_TEMPLATES_CMA_REPORT, {
    skip: !canInitializeTemplate,
    fetchPolicy: 'network-only',
  });
  const [updateCmaReport] = useMutation(UPDATE_CMA_REPORT_DOC_TEMPLATE);

  const [isInitializingTemplate, setIsInitializingTemplate] = useState(
    canInitializeTemplate,
  );

  const initializeTemplate = useCallback(async () => {
    try {
      if (
        canInitializeTemplate &&
        (docTemplatesData?.doc_templates?.length ?? 0) > 0
      ) {
        const defaultTemplateId = getDefaultTemplate(
          docTemplatesData?.doc_templates,
        )?.id;

        if (defaultTemplateId) {
          await updateCmaReport({
            variables: {
              id: cmaReport?.id,
              input: {
                doc_template_id: defaultTemplateId,
              },
            },
          });
        }
      }
    } finally {
      setIsInitializingTemplate(false);
    }
  }, [
    canInitializeTemplate,
    cmaReport?.id,
    docTemplatesData?.doc_templates,
    getDefaultTemplate,
    updateCmaReport,
  ]);

  useEffect(() => {
    initializeTemplate();
  }, [initializeTemplate]);

  const steps = useMemo<StepType<CMAPageId>[]>(() => {
    const result: StepType<CMAPageId>[] = [
      {
        title: t('Cover'),
        Component: StepCoverPhotoCMA,
        pageId: 'page-cover',
        icon: <FileIcon size={20} />,
        activeIcon: <FileIcon {...activeStepIconProps} />,
        active: cmaReport.include_cover,
        databaseToggleField: 'include_cover',
        position: 'main',
      },
      {
        title: t('Introduction'),
        Component: StepIntroduction,
        pageId: 'page-introduction',
        icon: <FileTextIcon size={20} />,
        activeIcon: <FileTextIcon {...activeStepIconProps} />,
        active: cmaReport.include_introduction,
        databaseToggleField: 'include_introduction',
        position: 'main',
      },
      {
        title: t('Property'),
        Component: StepPropertyDetailsCMA,
        pageId: 'page-property',
        icon: <HouseIcon size={20} />,
        activeIcon: <HouseIcon {...activeStepIconProps} />,
        active: cmaReport.include_property,
        databaseToggleField: 'include_property',
        position: 'main',
      },
      {
        title: t('Description'),
        Component: StepDescription,
        pageId: 'page-description',
        icon: <MessageCircleIcon size={20} />,
        activeIcon: <MessageCircleIcon {...activeStepIconProps} />,
        active: cmaReport.include_description,
        databaseToggleField: 'include_description',
        position: 'main',
      },
      {
        title: t('Photos'),
        Component: StepPhotos,
        pageId: 'page-photos',
        icon: <CameraIcon size={20} />,
        activeIcon: <CameraIcon {...activeStepIconProps} />,
        active: cmaReport.include_photos,
        databaseToggleField: 'include_photos',
        position: 'main',
      },
      {
        title: t('Points of Interest'),
        Component: StepPOICMA,
        pageId: 'page-point-of-interest',
        icon: <MapPinIcon size={20} />,
        activeIcon: <MapPinIcon {...activeStepIconProps} />,
        active: cmaReport.include_pois,
        databaseToggleField: 'include_pois',
        position: 'main',
      },
      {
        title: t('Market'),
        Component: StepMarketData,
        pageId: 'page-market-data',
        icon: <TrendingUpIcon size={20} />,
        activeIcon: <TrendingUpIcon {...activeStepIconProps} />,
        active: cmaReport.include_market_data,
        databaseToggleField: 'include_market_data',
        position: 'main',
      },
      {
        title: t('AVM'),
        Component: StepHedonisticValuation,
        pageId: 'page-hedonistic-valuation',
        icon: <HouseIcon size={20} />,
        activeIcon: <HouseIcon {...activeStepIconProps} />,
        active: cmaReport.include_hedonistic_valuation,
        databaseToggleField: 'include_hedonistic_valuation',
        position: 'main',
      },
      {
        title: t('Comparable listings'),
        Component: StepListingsCompare,
        pageId: 'page-comparables-list',
        icon: <Rows3Icon size={20} />,
        activeIcon: <Rows3Icon {...activeStepIconProps} />,
        active: cmaReport.include_comparables_valuation,
        databaseToggleField: 'include_comparables_valuation',
        position: 'main',
      },
      {
        title: t('Comparable rental listings'),
        Component: RentalListings,
        pageId: 'page-rental-comparables-list',
        icon: <Rows3Icon size={20} />,
        activeIcon: <Rows3Icon {...activeStepIconProps} />,
        active: cmaReport.include_rental_comparables,
        databaseToggleField: 'include_rental_comparables',
        position: 'main',
      },
      {
        title: t('Comparable transactions'),
        Component: StepTransactions,
        pageId: 'page-transactions',
        icon: <ReceiptTextIcon size={20} />,
        activeIcon: <ReceiptTextIcon {...activeStepIconProps} />,
        active: cmaReport.include_transactions,
        databaseToggleField: 'include_transactions',
        position: 'main',
      },
      {
        title: t('Cap rate valuation'),
        Component: StepCapRateValuation,
        pageId: 'page-cap-rate-valuation',
        icon: <PercentIcon size={20} />,
        activeIcon: <PercentIcon {...activeStepIconProps} />,
        active: cmaReport.include_cuprate_valuation,
        databaseToggleField: 'include_cuprate_valuation',
        position: 'main',
      },
      {
        title: t('Intrinsic valuation'),
        Component: StepIntrinsicValuation,
        pageId: 'page-intrinsic-valuation',
        icon: <HammerIcon size={20} />,
        activeIcon: <HammerIcon {...activeStepIconProps} />,
        active: cmaReport.include_intrinsic_valuation,
        databaseToggleField: 'include_intrinsic_valuation',
        position: 'main',
      },
      {
        title: t('Final value'),
        Component: StepFinal,
        pageId: 'page-final',
        icon: <DollarSignIcon size={20} />,
        activeIcon: <DollarSignIcon {...activeStepIconProps} />,
        active: cmaReport?.include_final_value,
        databaseToggleField: 'include_final_value',
        position: 'main',
      },
    ];

    // Add conditional steps
    if (countryCode === 'CH') {
      result.push({
        title: t('Potential buyers'),
        Component: StepPotentialBuyers,
        pageId: 'page-potential-buyers',
        icon: <UsersIcon size={20} />,
        activeIcon: <UsersIcon {...activeStepIconProps} />,
        active: cmaReport?.include_potential_buyers,
        databaseToggleField: 'include_potential_buyers',
        position: 'main',
      });
    }

    if (cmaReport.broker?.show_in_agency_pages) {
      result.push({
        title: t('Agent profile'),
        Component: StepAgentProfile,
        pageId: 'page-agent-profile',
        icon: <IdCardIcon size={20} />,
        activeIcon: <IdCardIcon {...activeStepIconProps} />,
        active: cmaReport?.include_agent,
        databaseToggleField: 'include_agent',
        position: 'main',
      });
    }

    if (cmaReport.broker?.default_team?.show_in_agency_pages) {
      result.push({
        title: t('Agency profile'),
        Component: StepAgencyProfile,
        pageId: 'page-agency-profile',
        icon: <Building2Icon size={20} />,
        activeIcon: <Building2Icon {...activeStepIconProps} />,
        active: cmaReport?.include_agent_agency,
        databaseToggleField: 'include_agent_agency',
        position: 'main',
      });
    }

    if (cmaReport.broker?.show_in_agency_pages) {
      result.push({
        title: t('Client testimonials'),
        Component: StepClientTestimonials,
        pageId: 'page-client-testimonials',
        icon: <MessageSquareTextIcon size={20} />,
        activeIcon: <MessageSquareTextIcon {...activeStepIconProps} />,
        active: cmaReport?.include_client_testimonial,
        databaseToggleField: 'include_client_testimonial',
        position: 'main',
      });
    }

    // Add the remaining steps
    result.push({
      title: t('Append files'),
      Component: StepAppendFiles,
      pageId: 'page-append-files',
      icon: <FilePlus2Icon size={20} />,
      activeIcon: <FilePlus2Icon {...activeStepIconProps} />,
      active: cmaReport.include_append_files,
      databaseToggleField: 'include_append_files',
      position: 'main',
    });

    result.push({
      title: t('Share report'),
      Component: StepShareReport,
      pageId: 'page-share',
      icon: <Share2Icon size={20} />,
      activeIcon: <Share2Icon {...activeStepIconProps} />,
      position: 'footer',
      navigable: false,
    });

    return result;
  }, [cmaReport, countryCode, t]);

  const includeFields = useMemo(
    () => extractFieldsFromFragment(CMA_REPORT_INCLUDE_FRAGMENT),
    [],
  );

  if (cmaReport.doc_template == null && !canInitializeTemplate) {
    return (
      <Alert severity="warning">
        {t('You do not have access to the template of this report')}
      </Alert>
    );
  }

  return (
    <ReportEditor<CMAPageId>
      isLoading={isInitializingTemplate}
      steps={steps}
      data={cmaReport}
      documentId={documentId}
      templateId={cmaReport.doc_template?.id ?? ''}
      documentType={Doc_Template_Type_Enum_Enum.CmaReport}
      language={cmaReport.language}
      exitPath="/cma-reports"
      includeFields={includeFields}
      previewOnly={previewOnly}
      previewVersion={previewVersion}
    />
  );
};

export default CMAReportEditorWorkflow;
